// A free replay's register page, /register/<slug>?src=<source> (docs/replay-register-spec.md, Adam's
// cut-down version of 23 Oct). 1 link in an email or an ad: the person types their first name and email
// and is watching the replay a few seconds later, on a phone. It opens logged out and logged in alike,
// ahead of the login door (App.jsx), and logs nobody in: everyone who registers gets the same answer, a
// pass that plays only this replay, kept in this browser (free/register.js). Logging in later is the
// usual email code, like any member.
//
// - Loading: the 16:9 box where the thumbnail goes, so the page doesn't move when it comes.
// - Not switched on, or no such link: "This replay isn't available at the moment."
// - The form, in the master brand and the Login board's look (Login.dc.html: the fields, the button and
//   the small print): Adam's 16:9 thumbnail, or the grey date panel, the title, the line, First name,
//   Email, a honeypot no person sees, "Watch the replay", and the line about emails with the privacy
//   policy. On a laptop the thumbnail sits beside the form; on a phone it's above it.
// - Watching: the replay page's own layout (screens/WebinarReplay.jsx, free.css): the player at the top,
//   then the title, the date and the description; its chapters and its call to action only once it
//   plays, and the call to action only when the answer has 1. The function gives it per person (Adam, 23
//   Oct: owners aren't pitched; free accounts see it): someone who owns what the replay sells gets none,
//   on the register answer and the replay alike. ReplayPlayer as it is, saving nothing (canSave false):
//   there's no login to save to.
// - The first name sent is the first word typed, after any title (free/register.js firstWordOf), as the function takes it.
// - Coming back to the same link with a pass still in date skips the form. An expired pass is let go and
//   the form shows again. Nothing a person types goes in an address: the 2 fields go in the POST's body.
import { useCallback, useEffect, useRef, useState } from 'react'
import { SITE, setTitle } from '../config/site.js'
import { ENV } from '../lib/env.js'
import { usePhone } from '../lib/media.js'
import { useQueryParam } from '../lib/router.jsx'
import { Wordmark } from '../shell/Marks.jsx'
import { Footer } from '../shell/Shell.jsx'
import { LoadFailed } from '../free/FreePage.jsx'
import { ReplayPlayer } from '../free/ReplayPlayer.jsx'
import { Dot } from '../free/WebinarDot.jsx'
import { ThumbnailFrame } from '../free/Thumbnail.jsx'
import { DateBlock } from '../library/DateBlock.jsx'
import {
  WORDS, registerSlugIn, callReplayRegister, firstNameProblem, firstWordOf, emailProblem, fieldWords,
  localStore, readPass, savePass, forgetPass, pageView, replayView,
} from '../free/register.js'
import '../styles/shell.css'
import '../styles/library.css'
import '../styles/free.css'
import '../styles/register.css'

const conn = () => ({ url: ENV.supabaseUrl, key: ENV.publishableKey })
const call = (action, body) => callReplayRegister(action, body, conn())

export default function Register({ path }) {
  const phone = usePhone()
  const slug = registerSlugIn(path)
  const src = useQueryParam('src')
  const [view, setView] = useState({ status: slug ? 'loading' : 'gone' })
  const seq = useRef(0)

  const start = useCallback(async () => {
    const n = ++seq.current
    const live = () => n === seq.current
    if (!slug) { setView({ status: 'gone' }); return }
    setView({ status: 'loading' })
    const store = localStore()
    // a pass still in date for this link: straight to the replay
    const pass = readPass(store, slug)
    if (pass) {
      const a = await call('replay', { pass })
      if (!live()) return
      if (a.ok && a.replay) { setView({ status: 'watching', replay: replayView(a.replay) }); return }
      if (a.error === 'gone') { setView({ status: 'gone' }); return }
      if (a.error !== 'expired') { setView({ status: 'failed' }); return }
      forgetPass(store, slug)
    }
    const p = await call('page', { slug })
    if (!live()) return
    if (p.ok && p.page) setView({ status: 'form', page: pageView(p.page) })
    else setView({ status: p.error === 'gone' ? 'gone' : 'failed' })
  }, [slug])
  useEffect(() => { start() }, [start])

  const title = view.status === 'form' ? view.page.title : view.status === 'watching' ? view.replay.title : WORDS.eyebrow
  useEffect(() => { setTitle(title || WORDS.eyebrow) }, [title])

  // registered: keep the pass for this link, and play
  async function registered(answer) {
    savePass(localStore(), slug, answer.pass, answer.pass_expires_at)
    let replay = answer.replay
    if (!replay) {
      const a = await call('replay', { pass: answer.pass })
      if (!a.ok || !a.replay) return a.error === 'gone' ? setView({ status: 'gone' }) : setView({ status: 'failed' })
      replay = a.replay
    }
    window.scrollTo(0, 0)
    setView({ status: 'watching', replay: replayView(replay), fresh: true })
  }

  return (
    <div className="rg-page">
      <header className={phone ? 'rg-header rg-header--phone' : 'rg-header'}>
        <Wordmark className={phone ? 'mark mark--phone' : 'mark mark--header'} />
      </header>
      <main className={`fp-main fp-main--replay rg-main rg-main--${view.status}`}>
        {view.status === 'loading' && (
          <div className="rg-intro" aria-busy="true">
            <div className="rg-art"><span className="thumb rg-thumb" /></div>
          </div>
        )}
        {view.status === 'failed' && <LoadFailed onRetry={start} />}
        {view.status === 'gone' && (
          <section className="rg-gone">
            <span className="rg-eyebrow">{WORDS.eyebrow}</span>
            <h1 className="rg-gone-title">{WORDS.gone}</h1>
          </section>
        )}
        {view.status === 'form' && (
          <RegisterForm page={view.page} slug={slug} src={src} onRegistered={registered} onGone={() => setView({ status: 'gone' })} />
        )}
        {view.status === 'watching' && <Watching key={view.replay.id || slug} r={view.replay} focus={Boolean(view.fresh)} />}
      </main>
      <Footer phone={phone} />
    </div>
  )
}

function RegisterForm({ page, slug, src, onRegistered, onGone }) {
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [errors, setErrors] = useState({ first_name: null, email: null })
  const [formError, setFormError] = useState(null)
  const [sending, setSending] = useState(false)
  const [thumbFailed, setThumbFailed] = useState(false)
  const nameRef = useRef(null)
  const emailRef = useRef(null)
  const buttonRef = useRef(null)
  // after a refusal (busy, failed), focus goes back to the button, which was disabled while it sent and
  // so let go of it: a keyboard or switch user carries on from where they pressed
  const [refocus, setRefocus] = useState(0)
  useEffect(() => { if (refocus) buttonRef.current?.focus() }, [refocus])

  const focusFirst = (bad) => {
    if (bad.first_name) nameRef.current?.focus()
    else if (bad.email) emailRef.current?.focus()
  }

  async function submit(e) {
    e.preventDefault()
    if (sending) return
    setFormError(null)
    const bad = { first_name: firstNameProblem(firstName), email: emailProblem(email) }
    setErrors(bad)
    if (bad.first_name || bad.email) { focusFirst(bad); return }
    setSending(true)
    const a = await call('register', {
      slug, first_name: firstWordOf(firstName), email: email.trim(), src: src ? src.slice(0, 200) : null, website,
    })
    if (a.ok && a.pass) { await onRegistered(a); return }
    setSending(false)
    if (a.error === 'first_name' || a.error === 'email') {
      const said = { first_name: a.error === 'first_name' ? 'shape' : null, email: a.error === 'email' ? 'shape' : null }
      setErrors(said)
      focusFirst(said)
      return
    }
    if (a.error === 'gone') { onGone(); return }
    setFormError(a.error === 'busy' ? WORDS.busy : WORDS.failed)
    setRefocus(n => n + 1)
  }

  const nameError = fieldWords('first_name', errors.first_name)
  const emailError = fieldWords('email', errors.email)
  const thumb = { url: thumbFailed ? null : page.thumbnailUrl, pending: false }

  return (
    <section className="rg-intro" aria-labelledby="rg-title">
      <div className="rg-art">
        <ThumbnailFrame thumb={thumb} className="rg-thumb" onError={() => setThumbFailed(true)}>
          {page.block && <DateBlock block={page.block} />}
        </ThumbnailFrame>
      </div>
      <div className="rg-side">
        <div className="rg-text">
          <h1 className="rg-title" id="rg-title">{page.title}</h1>
          <p className="rg-line">{WORDS.line}</p>
        </div>
        {/* method post: should the page ever submit on its own, nothing typed goes in the address */}
        <form className="rg-form" method="post" noValidate onSubmit={submit}>
          <div className="rg-field">
            <label htmlFor="rg-first-name">{WORDS.firstName}</label>
            <input id="rg-first-name" ref={nameRef} name="first_name" type="text" autoComplete="given-name" autoCapitalize="words"
              spellCheck="false" maxLength={60} value={firstName} onChange={e => setFirstName(e.target.value)}
              aria-invalid={nameError ? true : undefined} aria-describedby={nameError ? 'rg-first-name-error' : undefined} />
            {nameError && <p className="rg-error" id="rg-first-name-error" role="alert">{nameError}</p>}
          </div>
          <div className="rg-field">
            <label htmlFor="rg-email">{WORDS.email}</label>
            <input id="rg-email" ref={emailRef} name="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none"
              spellCheck="false" maxLength={254} value={email} onChange={e => setEmail(e.target.value)}
              aria-invalid={emailError ? true : undefined} aria-describedby={emailError ? 'rg-email-error' : undefined} />
            {emailError && <p className="rg-error" id="rg-email-error" role="alert">{emailError}</p>}
          </div>
          {/* the honeypot: off the screen, out of the tab order and hidden from screen readers, so only a
              bot fills it in; the function answers it the same, and makes, sends and records nothing */}
          <div className="rg-hp" aria-hidden="true">
            <label htmlFor="rg-website">{WORDS.website}</label>
            <input id="rg-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} />
          </div>
          <button type="submit" className="rg-btn" ref={buttonRef} disabled={sending} aria-busy={sending || undefined}>{WORDS.button}</button>
          {formError && <p className="rg-error" role="alert">{formError}</p>}
          <p className="rg-small">
            {WORDS.smallPrint} <a href={SITE.privacyUrl} target="_blank" rel="noopener noreferrer">{WORDS.privacy}</a>
          </p>
        </form>
      </div>
    </section>
  )
}

// The replay, as /free-webinars/replay/<id> draws it (WebinarReplay's Replay), with nothing saved
function Watching({ r, focus }) {
  const [poster, setPoster] = useState(r.thumbnailUrl)
  const [seek, setSeek] = useState(null)
  // pressed Play, and it's playing: only then the chapters and the call to action
  const [started, setStarted] = useState(false)
  const heading = useRef(null)
  useEffect(() => { if (focus) heading.current?.focus({ preventScroll: true }) }, [focus])
  // known before Play, so the player is the same size before and after it; with nothing to come beside
  // it, 1 column (the replay page's fw-watch--solo, its review of 23 Oct)
  const hasSide = r.chapters.length > 0 || Boolean(r.cta)
  const side = started && hasSide
  return (
    <article className={hasSide ? 'fw-watch' : 'fw-watch fw-watch--solo'} aria-labelledby="rg-watch-title">
      <div className="fw-watch-main">
        <ReplayPlayer title={r.title} vimeo={r.vimeo} savedSeconds={0} seek={seek} onPosition={() => {}}
          onStart={() => setStarted(true)} canSave={false} poster={poster} onPosterError={() => setPoster(null)} />
        <div className="fw-watch-text">
          <span className="fw-status"><Dot look={r.sellsLook} name={r.sellsName} />{WORDS.replay}</span>
          <h1 id="rg-watch-title" className="fw-watch-title" ref={heading} tabIndex={-1}>{r.title}</h1>
          {r.date && <span className="fw-time">{r.date}</span>}
          {r.description && <p className="fw-desc fw-watch-desc">{r.description}</p>}
        </div>
      </div>
      {side && (
        <div className="fw-side">
          {r.chapters.length > 0 && (
            <div className="fw-chapters">
              <h2 className="fw-chapters-head">{WORDS.chapters}</h2>
              {r.chapters.map((c, i) => (
                <button key={i} type="button" className="fw-chap" onClick={() => setSeek({ seconds: c.seconds, n: Date.now() })}>
                  <span className="fw-chap-time">{c.time}</span><span className="fw-chap-title">{c.title}</span>
                </button>
              ))}
            </div>
          )}
          {r.cta && (
            <div className="fw-box">
              {r.cta.sellsName && <span className="fw-box-eyebrow"><Dot look={r.cta.sellsLook} name={r.cta.sellsName} />{r.cta.sellsName}</span>}
              {r.cta.text && <span className="fw-box-line">{r.cta.text}</span>}
              <a className="fw-box-btn" href={r.cta.url} target="_blank" rel="noopener noreferrer">{r.cta.label}</a>
            </div>
          )}
        </div>
      )}
    </article>
  )
}
